import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // ✅ Only these pages get transparent header at top
  const transparentPaths = ['/', '/about', '/projects'];

  const isActive = (path) => location.pathname === path;

  const navLinkClass = (path, isTransparent) =>
    isActive(path)
      ? isTransparent
        ? 'text-white font-bold border-b-2 border-white pb-1'
        : 'text-green-600 font-bold border-b-2 border-green-600 pb-1'
      : isTransparent
      ? 'text-white/80 hover:text-white transition-colors'
      : 'text-blue-500 hover:text-green-500 transition-colors';

  const mobileNavLinkClass = (path) =>
    isActive(path)
      ? 'text-green-600 font-bold bg-green-50 rounded-lg px-4'
      : 'text-blue-500 hover:text-green-500 px-4';

  // ✅ Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) setIsScrolled(true);
      else setIsScrolled(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ✅ Transparent only on hero of /, /about, /projects and only at top
  const isTransparent = transparentPaths.includes(location.pathname) && !isScrolled;

  // ✅ Logo path (single logo)
  const currentLogo = '/images/HPR_LOGO.png';

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isTransparent
            ? 'bg-transparent shadow-none'
            : 'bg-white shadow-md backdrop-blur-sm bg-opacity-95'
        }`}
      >
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src={currentLogo}
                alt="HPR Infra Logo"
              className="h-10 sm:h-12 md:h-14 lg:h-16 xl:h-16"

              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <Link to="/" className={`${navLinkClass('/', isTransparent)} text-lg font-semibold`}>
                Home
              </Link>
              <Link
                to="/about"
                className={`${navLinkClass('/about', isTransparent)} text-lg font-semibold`}
              >
                About Us
              </Link>
              <Link
                to="/projects"
                className={`${navLinkClass('/projects', isTransparent)} text-lg font-semibold`}
              >
                Projects
              </Link>
              <Link
                to="/blogs"
                className={`${navLinkClass('/blogs', isTransparent)} text-lg font-semibold`}
              >
                Blogs
              </Link>
              <Link
                to="/career"
                className={`${navLinkClass('/career', isTransparent)} text-lg font-semibold`}
              >
                Career
              </Link>
              <Link
                to="/contact"
                className={`${navLinkClass('/contact', isTransparent)} text-lg font-semibold`}
              >
                Contact
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-3 sm:space-x-4">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="relative w-8 h-8 flex flex-col justify-center items-center"
                aria-label="Toggle menu"
              >
                <span
                  className={`block h-0.5 w-6 transition-all duration-300 ${
                    isTransparent ? 'bg-white' : 'bg-gray-800'
                  } ${mobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}
                ></span>
                <span
                  className={`block h-0.5 w-6 transition-all duration-300 my-1 ${
                    isTransparent ? 'bg-white' : 'bg-gray-800'
                  } ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}
                ></span>
                <span
                  className={`block h-0.5 w-6 transition-all duration-300 ${
                    isTransparent ? 'bg-white' : 'bg-gray-800'
                  } ${mobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}
                ></span>
              </button>
            </div>
          </nav>

          {/* Mobile Navigation */}
          <div
            className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl z-50 transition-all duration-300 ${
              mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
            }`}
          >
            <div className="px-4 py-6">
              <div className="flex flex-col space-y-3">
                {['/', '/about', '/projects', '/blogs', '/career', '/contact'].map((path) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${mobileNavLinkClass(path)} text-xl font-semibold py-2 transition-all`}
                  >
                    {path === '/'
                      ? 'Home'
                      : path.replace('/', '').replace(/^\w/, (c) => c.toUpperCase())}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
